import {
  HttpErrorResponse,
  HttpEvent,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { inject } from '@angular/core';
import { IApiError } from '@appTypes/common.types';
import { authLogOut, authVerifyAccessToken } from '@core/modules/auth/state/auth.actions';
import { AuthState } from '@core/modules/auth/state/auth.state';
import { LOG_IN_PAGE } from '@data/app.data';
import { environment } from '@env/environment';
import { Store } from '@ngxs/store';
import { catchError, filter, from, map, Observable, switchMap, take, throwError } from 'rxjs';
import { AppPersistedState } from '@app-state/app.persisted.state';
import {
  API_ACCEPT_JSON,
  API_ACCEPT_SVG,
  API_AUTHORIZATION_PREFIX,
  API_AUTH_URL_SEGMENT,
  API_CHANGE_PASSWORD_SEGMENT,
  API_DEFAULT_LANGUAGE,
  API_ERROR_LOG_PREFIX,
  API_FORGED_ERROR_STATUS,
  API_HEADER_ACCEPT,
  API_HEADER_ACCEPT_LANGUAGE,
  API_HEADER_AUTHORIZATION,
  API_HEADER_PREFER,
  API_HEADER_RESULT_ERROR,
  API_HEADER_RESULT_ERROR_CODE,
  API_HEADER_RESULT_SUCCESS,
  API_NGROK_SKIP_HEADER,
  API_RESULT_FALSE,
  API_SVG_URL_SUFFIX
} from './api.interceptor.const';
import { TRACEPARENT_HEADER, displayTraceId, newTraceparent, traceIdOf } from './trace-context';

/** Structured error payload emitted by `ApiInterceptor` when a request fails. */
export class ApiErrorResponse<T = unknown> {
  status?: number;
  headers?: HttpHeaders;
  url?: string;
  statusText?: string;
  error?: T;
  body?: T;
  sysErrorMessage?: string;
  displayErrorMessage?: string;
  errorCode?: string;
  activityId?: string;

  constructor(init: ApiErrorResponse<T>) {
    Object.assign(this, init);
  }
}

/** Attempts to parse a `Blob` error payload as JSON. Returns `null` on failure. */
const parseBlobError = async (blob: Blob): Promise<IApiError | null> => {
  try {
    const text = await blob.text();
    return JSON.parse(text);
  } catch {
    return null;
  }
};

/** Resolves the system error message from the parsed error, Nugios header, or response message. */
const resolveSysErrorMessage = (e: HttpErrorResponse, errorResponse: IApiError | null): string => {
  if (errorResponse?.errorDescription) return errorResponse.errorDescription;
  const headerMessage = e?.headers?.get(API_HEADER_RESULT_ERROR);
  if (headerMessage) return decodeURI(headerMessage);
  return e.message;
};

/**
 * Resolves the "Trace ID" shown to the agent so support finds the request in every service's logs (C23). The trace id
 * this browser sent in `traceparent` wins - every service continues it. Without one, the `traceId` of the answer (C23:
 * the website APIs and the gateway both carry it, online-api#9; also the `ProblemDetails` id of failures raised before the
 * Nugios layer), else the Nugios `activityId` (a W3C activity id, reduced to its trace id) of an older answer.
 */
export const resolveActivityId = (errorResponse: IApiError | null, sentTraceId?: string): string | undefined =>
  sentTraceId ?? displayTraceId(errorResponse?.traceId ?? errorResponse?.activityId);

/**
 * The error response a results box stores (website-web#47, C23): the JSON body of the answer when it is an object (so a screen can read its
 * `errorCode` / `providersUnavailable`), otherwise the interceptor's own error - and ALWAYS the Trace ID in `activityId`: the one this browser
 * sent in `traceparent` (an ingress 502 / 504, a timeout or an HTML body has no id of its own). Before, the raw body replaced the error and the
 * box showed no Trace ID.
 */
export const errorResponseWithTrace = (e: unknown): ApiErrorResponse => {
  const error = e as { error?: unknown; activityId?: string } | null | undefined;
  const rawBody = error?.error;
  const body = rawBody !== null && typeof rawBody === 'object' ? (rawBody as Record<string, unknown>) : null;
  const base = (body ?? error ?? {}) as Record<string, unknown>;
  const activityId =
    error?.activityId ?? (base['activityId'] as string | undefined) ?? (base['traceId'] as string | undefined);
  return { ...base, activityId };
};

/**
 * Creates an `ApiErrorResponse` by merging `HttpErrorResponse` fields with the parsed error.
 *
 * The Nugios error-code header wins; a code carried only in the body is the next choice —
 * file endpoints answer a refused download as JSON (`DOCUMENT_UNAVAILABLE_FOR_DOWNLOAD`)
 * with no header, and without this fallback the code would collapse to the generic
 * `HttpErrorResponse` name.
 */
const createApiErrorResponse = (
  e: HttpErrorResponse,
  errorResponse: IApiError | null,
  sentTraceId?: string
): ApiErrorResponse => {
  const headerErrorCode = e?.headers?.get(API_HEADER_RESULT_ERROR_CODE);
  return new ApiErrorResponse({
    errorCode: headerErrorCode ?? errorResponse?.errorCode ?? e.name,
    displayErrorMessage: errorResponse?.userErrorDescription,
    sysErrorMessage: resolveSysErrorMessage(e, errorResponse),
    activityId: resolveActivityId(errorResponse, sentTraceId),
    ...(e as ApiErrorResponse)
  });
};

/** Returns `true` when a 401 error must trigger an auto log-out. */
const shouldLogOutOn401 = (e: HttpErrorResponse): boolean =>
  e.status === 401 && !window.location.pathname.includes(LOG_IN_PAGE);

/** Parses the raw error payload into an `IApiError` when possible, preserving async timing. */
const parseErrorPayload = (e: HttpErrorResponse): Observable<IApiError | null> =>
  e.error instanceof Blob ? from(parseBlobError(e.error)) : from(Promise.resolve(e.error as IApiError | null));

/** Normalizes any HTTP error into an `ApiErrorResponse` and triggers auto log-out when required. */
const handleErrorResponse = (e: HttpErrorResponse, store: Store, sentTraceId?: string): Observable<never> => {
  if (shouldLogOutOn401(e)) {
    store.dispatch(new authLogOut(true));
  }

  return parseErrorPayload(e).pipe(
    switchMap((errorResponse) => {
      const apiErrorResponse = createApiErrorResponse(e, errorResponse, sentTraceId);
      console.error(`${API_ERROR_LOG_PREFIX}: ${apiErrorResponse.sysErrorMessage}`, apiErrorResponse);
      return throwError(() => apiErrorResponse);
    })
  );
};

/**
 * Converts a `2xx` response flagged as logically unsuccessful by the
 * `X-Nugios-Result-Success` header into a thrown `HttpErrorResponse`.
 */
const handleSuccessResponse = (event: HttpEvent<unknown>): HttpEvent<unknown> => {
  if (!(event instanceof HttpResponse) || !event.ok) return event;
  const nugiosResultSuccess = event.headers.get(API_HEADER_RESULT_SUCCESS)?.toLowerCase();
  if (nugiosResultSuccess !== API_RESULT_FALSE) return event;
  throw new HttpErrorResponse({
    status: API_FORGED_ERROR_STATUS,
    headers: event.headers,
    url: event.url ?? '',
    error: event.body
  });
};

/** Computes the `Accept` header value based on the request URL. */
const resolveAcceptHeader = (request: HttpRequest<unknown>): string[] =>
  request.url.endsWith(API_SVG_URL_SUFFIX) ? [API_ACCEPT_SVG] : [API_ACCEPT_JSON];

/**
 * Clones the request with the standard API headers applied. Calls to the website API also start a trace
 * (`traceparent`, C23); other hosts are not sent one, so a third party's CORS policy never sees an unexpected header.
 */
const addApiRequestHeaders = (request: HttpRequest<unknown>, store?: Store): HttpRequest<unknown> => {
  const setHeaders: Record<string, string | string[]> = {
    [API_HEADER_ACCEPT]: resolveAcceptHeader(request),
    [API_HEADER_ACCEPT_LANGUAGE]: store?.selectSnapshot(AppPersistedState.language) ?? API_DEFAULT_LANGUAGE,
    [API_HEADER_PREFER]: `timezone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`
  };
  if (request.url.includes(environment.urls.api) && !request.headers.has(TRACEPARENT_HEADER))
    setHeaders[TRACEPARENT_HEADER] = newTraceparent();
  const accessToken = store?.selectSnapshot(AuthState.accessToken);
  if (accessToken) setHeaders[API_HEADER_AUTHORIZATION] = `${API_AUTHORIZATION_PREFIX} ${accessToken}`;

  return request.clone({
    setHeaders,
    ...(accessToken ? { withCredentials: true } : {})
  });
};

/** Propagates the ngrok bypass header (as both name and value) when set by the caller. */
const appendNgrokHeaderIfPresent = (request: HttpRequest<unknown>): HttpRequest<unknown> => {
  if (!request.headers.get(API_NGROK_SKIP_HEADER)) return request;
  const modifiedHeaders = request.headers.append(API_NGROK_SKIP_HEADER, API_NGROK_SKIP_HEADER);
  return request.clone({ headers: modifiedHeaders });
};

/** Determines whether the request must bypass access-token verification. */
const bypassesTokenVerification = (request: HttpRequest<unknown>): boolean => {
  if (!request.url.includes(environment.urls.api)) return true;
  return request.url.includes(API_AUTH_URL_SEGMENT) && !request.url.includes(API_CHANGE_PASSWORD_SEGMENT);
};

/**
 * API interceptor responsible for:
 * - Propagating the ngrok bypass header when present.
 * - Adding standard API headers (Accept, Accept-Language, Prefer, Authorization) and starting the trace (traceparent).
 * - Verifying the access token before dispatching API calls (except `/auth/*` non-ChangePassword).
 * - Normalizing API errors into `ApiErrorResponse`.
 * - Converting logically-failed `2xx` responses into errors.
 */
export const ApiInterceptor: HttpInterceptorFn = (request, next) => {
  const ngrokAwareRequest = appendNgrokHeaderIfPresent(request);
  const store = inject(Store);

  if (bypassesTokenVerification(ngrokAwareRequest)) {
    return next(addApiRequestHeaders(ngrokAwareRequest));
  }

  return store.dispatch(new authVerifyAccessToken()).pipe(
    switchMap(() => store.select(AuthState.refreshTokenLoadStatus)),
    filter((status) => status?.status !== 'loading'),
    take(1),
    map(() => addApiRequestHeaders(ngrokAwareRequest, store)),
    switchMap((preparedRequest) =>
      next(preparedRequest).pipe(
        map(handleSuccessResponse),
        catchError((e: HttpErrorResponse) =>
          handleErrorResponse(e, store, traceIdOf(preparedRequest.headers.get(TRACEPARENT_HEADER)))
        )
      )
    )
  );
};
